import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '@/components/Icon';
import Topbar from '@/layout/Topbar';
import { useShell } from '@/layout/AppShell';
import { useCan } from '@/auth/AuthContext';
import { api } from '@/lib/api';

// Ключ персональной настройки для избранных отчётов (хранится в БД per-user,
// одинаково на любом устройстве). Значение — массив id отчётов.
const FAV_PREF_KEY = 'report_favorites';

/**
 * Каталог доступных отчётов, сгруппированный по разделам. По мере добавления
 * новых отчётов — расширять `GROUPS`. Конкретные отчёты живут на собственных
 * подстраницах под `/reports/<id>`.
 */
const GROUPS = [
  {
    title: 'Финансовые отчёты',
    icon:  'money',
    items: [
      {
        id:    'extras-by-student-monthly',
        title: 'Доп. занятия по ученикам за месяц',
        desc:  'Календарная сетка проведённых доп. занятий с разбивкой по ученикам и предметам, с суммами.',
        to:    '/reports/extras-by-student-monthly',
      },
      {
        id:    'extras-by-teacher-monthly',
        title: 'Доп. занятия по учителям за месяц',
        desc:  'Сводка проведённых доп. занятий по каждому учителю — индивидуальные с разбивкой по ученикам и групповые с разбивкой по слотам, с суммой к выплате.',
        to:    '/reports/extras-by-teacher-monthly',
      },
      {
        id:    'unpaid-next-month',
        title: 'Поиск неоплативших следующий месяц',
        desc:  'Берём действующие основные договоры (сад/школа), сравниваем сумму начислений с начала договора по выбранный месяц с уже оплаченным — и показываем тех, кому не хватает.',
        to:    '/reports/unpaid-next-month',
      },
      {
        id:    'extras-balances',
        title: 'Балансы по доп. услугам',
        desc:  'Сводка приход / расход / баланс по каждому ученику с договором на доп. услуги на выбранный учебный год. Клик по числу — детализация: поступления, проведённые занятия и баланс по предметам.',
        to:    '/reports/extras-balances',
      },
      {
        id:    'extras-subject-balance',
        title: 'Баланс по дополнительной услуге',
        desc:  'Одна услуга × все ученики за учебный год: сколько оплачено, потрачено и каков остаток — в рублях и занятиях. Нулевые остатки можно скрыть.',
        to:    '/reports/extras-subject-balance',
      },
      {
        id:    'payments-chart',
        title: 'График платежей по месяцам',
        desc:  'Столбчатый график успешных оплат за выбранный месяц: по дням, в разбивке по категориям (школа / сад / взносы / доп. услуги) и способам оплаты (нал / карта / ЮКасса / банк / корректировка).',
        to:    '/reports/payments-chart',
      },
    ],
  },
  {
    title: 'Внеурочные занятия',
    icon:  'sparkle',
    items: [
      {
        id:    'extracurricular-by-student-monthly',
        title: 'Внеурочные занятия по ученикам за месяц',
        desc:  'Календарная сетка проведённых внеурочных занятий с разбивкой по ученикам и предметам — без денежных колонок (внеурочка бесплатная).',
        to:    '/reports/extracurricular-by-student-monthly',
      },
      {
        id:    'extracurricular-by-teacher-monthly',
        title: 'Внеурочные занятия по учителям за месяц',
        desc:  'По каждому учителю — список предметов и слотов времени, с разбивкой по дням: проведённых занятий и ученико-посещений.',
        to:    '/reports/extracurricular-by-teacher-monthly',
      },
      {
        id:    'extracurricular-popularity',
        title: 'Популярность внеурочных занятий',
        desc:  'Столбчатый график с количеством ученико-посещений по каждому предмету за выбранный период (учебный год или конкретный месяц). Под графиком — таблица с цифрами и процентами.',
        to:    '/reports/extracurricular-popularity',
      },
    ],
  },
  {
    title: 'Образование',
    icon:  'graduation',
    items: [
      {
        id:    'unassigned-students',
        title: 'Поиск нераспределённых учеников с договорами',
        desc:  'Ученики, у которых на выбранный учебный год есть основной договор (сад/школа), но они ещё не зачислены ни в один класс / группу детсада на этот год.',
        to:    '/reports/unassigned-students',
      },
      {
        id:    'students-no-lk-access',
        title: 'Дети без доступа родителей в ЛК',
        desc:  'Активные ученики, у которых ни один привязанный родитель не может войти в личный кабинет: учётка не привязана, деактивирована, без email или без пароля (приглашение не завершено). По каждому — расшифровка, что чинить.',
        to:    '/reports/students-no-lk-access',
      },
      {
        id:    'class-lists',
        title: 'Списки классов',
        desc:  'Списки учеников по каждому классу / группе детсада на выбранный учебный год: за весь год с указанием периода или на конкретную дату. Экспорт в Word — каждый класс на отдельной странице.',
        to:    '/reports/class-lists',
      },
      {
        id:    'age-sex',
        title: 'Половозрастной состав',
        desc:  'Сколько мальчиков и девочек каждого возраста учится на дату составления отчёта (полных лет). Опциональная разбивка по группам детсада и классам.',
        to:    '/reports/age-sex',
      },
      {
        id:    'vacation-lessons',
        title: 'Ошибочно проставленные занятия (каникулы)',
        desc:  'Записи журнала — темы уроков и ДЗ, — чьи даты оказались на каникулах или выходных производственного календаря (журнал заполнили заранее, календарь внесли позже). Показывает и выставленные на эти даты оценки.',
        to:    '/reports/vacation-lessons',
      },
    ],
  },
  {
    title: 'Договоры',
    icon:  'doc',
    items: [
      {
        id:    'agreements-registry',
        title: 'Реестр договоров',
        desc:  'Плоская таблица всех договоров с фильтрами по дате/году/типу/классу/подписи/скану/архиву. Сортировка ФИО или по дате договора. Экспорт в Excel.',
        to:    '/reports/agreements-registry',
      },
      {
        id:    'payments-by-new-agreements',
        title: 'Оплаты по новым договорам',
        desc:  'Договоры, заключённые в выбранный период (по умолчанию — прошлый месяц). По каждому — оплачен ли вступительный взнос (план/факт) и сумма ежемесячных оплат. Фильтр «Школа / Сад / Все». Экспорт в Excel.',
        to:    '/reports/payments-by-new-agreements',
      },
      {
        id:    'terminations',
        title: 'Причины досрочного расторжения',
        desc:  'Расторгнутые договоры за выбранный учебный год / диапазон дат: ФИО ученика, номер и дата договора, период действия, дата расторжения, причина и подробности («Перевод в другую школу: куда»). По умолчанию — только с указанной причиной; галку можно снять, чтобы увидеть всех.',
        to:    '/reports/terminations',
      },
    ],
  },
  {
    title: 'Карты',
    icon:  'pin',
    items: [
      {
        id:    'students-on-map',
        title: 'Ученики на карте',
        desc:  'Точки на Яндекс.Карте по адресам подписантов действующих договоров. Несколько учеников по одному адресу группируются в один маркер. Фильтры: тип договора (школа / сад), архивные, поиск по адресу / ФИО.',
        to:    '/reports/students-on-map',
      },
    ],
  },
  {
    title: 'СКУД',
    icon:  'badge',
    perm:  'skud.reports',   // группа видна только с правом skud.reports/read
    items: [
      {
        id:    'skud-first-last',
        title: 'Первый и последний проход за месяц',
        desc:  'По выбранному подразделению и месяцу — таблица первого и последнего прохода каждого сотрудника по дням. Галка «показывать уволенных».',
        to:    '/reports/skud-first-last',
      },
      {
        id:    'skud-route',
        title: 'Маршрут движения человека',
        desc:  'Выбираем человека и период — хронологический список всех его событий СКУД: время, тип, точка доступа, направление, пропуск.',
        to:    '/reports/skud-route',
      },
    ],
  },
  {
    title: 'Администрирование',
    icon:  'users',
    items: [
      {
        id:    'parents-no-active-children',
        title: 'Родители без активных детей',
        desc:  'Родительские учётки, у которых все привязанные дети в архиве (или детей нет вовсе) — кандидаты на отключение. Показывает детей и привязанного человека СКУД: действующий пропуск подсвечен. Массовое отключение учёток и пропусков.',
        to:    '/reports/parents-no-active-children',
      },
      {
        id:    'skud-parents-unlinked',
        title: 'Родители в СКУД без учётки',
        desc:  'Люди из СКУД-подразделения родителей, на которых не ссылается ни одна учётка CRM: осиротевшие записи с пропусками. Действующие пропуска подсвечены.',
        to:    '/reports/skud-parents-unlinked',
      },
      {
        id:    'mobile-devices',
        title: 'Мобильные приложения',
        desc:  'Кто входил через мобильное приложение: пользователь, платформа (iPhone/Android), модель устройства, версия приложения, первый вход и последний запуск. Фильтры по платформе и роли.',
        to:    '/reports/mobile-devices',
      },
    ],
  },
  {
    title: 'Технические',
    icon:  'gear',
    items: [
      {
        id:    'user-actions',
        title: 'Действия пользователей',
        desc:  'Журнал событий из историй учеников от автора: кто, когда, с каким учеником и что сделал — договоры, зачисления, контакты, платежи. Фильтры по периоду, сотруднику, типу события и поиску.',
        to:    '/reports/user-actions',
        perm:  'reports.user_actions',
      },
      {
        id:    'api-log',
        title: 'Лог API-запросов',
        desc:  'Журнал всех запросов к API: кто, когда и что запрашивал — метод, путь, параметры (без паролей и файлов), IP, браузер, код ответа и длительность. Фильтры по датам, пользователю, пути, коду.',
        to:    '/reports/api-log',
        perm:  'reports.api_log',
      },
      {
        id:    'push-outbox',
        title: 'Очередь пушей',
        desc:  'Очередь исходящих пуш-уведомлений (push_outbox): получатель, заголовок и текст, статус отправки, попытки и ошибка FCM/APNs. Фильтры по статусу, периоду и получателю.',
        to:    '/reports/push-outbox',
        perm:  'reports.push_outbox',
      },
      {
        id:    'webserver-log',
        title: 'Лог веб-сервера',
        desc:  'Access-лог nginx: всё, что не дошло до PHP — ошибки статики, запросы старых билдов, обрывы, реальные IP и юзер-агенты. Фильтры по IP, методу, коду, пути и User-Agent.',
        to:    '/reports/webserver-log',
        perm:  'reports.webserver_log',
      },
    ],
  },
  // Сюда позже — «По учителям», «Долги по платежам», «Сверка с 1С» и т. д.
];

// Плоский индекс всех отчётов по id — чтобы собрать секцию «Избранное».
const ITEMS_BY_ID = {};
for (const g of GROUPS) for (const it of g.items) ITEMS_BY_ID[it.id] = it;

export default function ReportsIndex() {
  const { tweaks, updateTweak } = useShell();
  const can = useCan();

  // Избранное. Массив id в порядке добавления. Грузим из БД (per-user),
  // чтобы одинаково работало на любом устройстве.
  const [favorites, setFavorites] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get(`/api/me/preferences/${FAV_PREF_KEY}`)
      .then((res) => {
        const val = Array.isArray(res?.value) ? res.value : [];
        // Оставляем только существующие отчёты (на случай, если отчёт убрали).
        setFavorites(val.filter((id) => ITEMS_BY_ID[id]));
      })
      .catch(() => { /* нет настройки/ошибка — просто пусто */ })
      .finally(() => setLoaded(true));
  }, []);

  const favSet = useMemo(() => new Set(favorites), [favorites]);

  const toggleFav = (id) => {
    const next = favSet.has(id)
      ? favorites.filter((x) => x !== id)
      : [...favorites, id];
    const prev = favorites;
    setFavorites(next); // оптимистично
    api.put(`/api/me/preferences/${FAV_PREF_KEY}`, { value: next })
      .catch(() => setFavorites(prev)); // откат при ошибке
  };

  const renderCard = (it) => {
    const isFav = favSet.has(it.id);
    return (
      <Link key={it.id} to={it.to} className="reports-card">
        <span className="reports-card__icon"><Icon name="chart" size={18} /></span>
        <div className="reports-card__body">
          <div className="reports-card__title">{it.title}</div>
          <div className="reports-card__desc">{it.desc}</div>
        </div>
        <button
          type="button"
          className={'reports-card__star' + (isFav ? ' is-fav' : '')}
          title={isFav ? 'Убрать из избранного' : 'В избранное'}
          aria-pressed={isFav}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFav(it.id); }}
        >
          <Icon name="star" size={16} />
        </button>
        <span className="reports-card__arrow"><Icon name="chevron" size={14} /></span>
      </Link>
    );
  };

  return (
    <>
      <Topbar
        breadcrumb={['Отчёты']}
        density={tweaks.density}
        onDensityChange={(d) => updateTweak('density', d)}
      />
      <div className="reports-page">
        {/* Избранное — вверху, в порядке добавления. Отчёты, помеченные
            звездой, показываются здесь и убираются из своих категорий. */}
        {loaded && favorites.length > 0 && (
          <section className="reports-group">
            <h2 className="reports-group__title reports-group__title--fav">
              <Icon name="star" size={13} /> Избранное
            </h2>
            <div className="reports-group__list">
              {favorites.map((id) => ITEMS_BY_ID[id]).filter(Boolean).map(renderCard)}
            </div>
          </section>
        )}

        {GROUPS.map((g) => {
          // Право проверяется на уровне отчёта (item.perm), у группы —
          // общий фолбэк (g.perm). Группа без единого доступного отчёта
          // скрывается целиком.
          if (g.perm && !can(g.perm, 'read')) return null;
          const items = g.items.filter((it) => !it.perm || can(it.perm, 'read'));
          if (items.length === 0) return null;
          // Отчёты остаются в своих категориях (в т.ч. избранные — с жёлтой
          // звездой); секция «Избранное» выше их просто дублирует сверху.
          return (
            <section key={g.title} className="reports-group">
              <h2 className="reports-group__title">{g.title}</h2>
              <div className="reports-group__list">
                {items.map(renderCard)}
              </div>
            </section>
          );
        })}
      </div>
    </>
  );
}
